import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import NoSsr from '@mui/material/NoSsr';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import Grow from '@mui/material/Grow';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import { muiGlobalTheme } from '@themes';
import { colors } from '../../../css';
import { Text } from '../../Text';
import { ReactChildrenPropType, WithStylesClassesPropType } from '../../../../lib/commonPropTypes';

let Select = <React.Component />;

const baseStyles = (theme) => ({
  input: {
    display: 'flex',
    padding: 0,
    height: theme.spacing(4)
  },
  valueContainer: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
    margin: '2px'
  },
  noOptionsMessage: {
    padding: `${theme.spacing(1)} calc(${theme.spacing(1)} * 2)`
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16
  },
  paper: {
    position: 'relative',
    zIndex: 1,
    marginTop: theme.spacing(1),
    left: 0
  },
  palceholderError: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
    color: 'red'
  }
});

const StyledCopyMini = styled(Text.CopyMini)`
  margin: 10px;
`;

function NoOptionsMessage(props) {
  const {
    children,
    selectProps: {
      classes: { noOptionsMessage }
    },
    innerProps
  } = props;
  return (
    <Typography color="textSecondary" className={noOptionsMessage} {...innerProps}>
      {children}
    </Typography>
  );
}

NoOptionsMessage.propTypes = {
  selectProps: PropTypes.shape({
    error: PropTypes.bool,
    classes: WithStylesClassesPropType,
    isRequired: PropTypes.bool
  }),
  children: ReactChildrenPropType,
  innerProps: PropTypes.shape({})
};

NoOptionsMessage.defaultProps = {
  selectProps: {},
  children: undefined,
  innerProps: {}
};

function inputComponent({ inputRef, id, ...props }) {
  return <div id={id} ref={inputRef} {...props} />;
}

function Control(props) {
  const { selectProps, innerRef, children, innerProps } = props;
  const {
    textFieldProps: { strikethrough },
    isDisabled,
    error,
    errorText,
    classes: { input },
    placeholder
  } = selectProps;

  return (
    <TextField
      style={{
        textDecoration: strikethrough ? 'line-through' : 'none',
        whiteSpace: 'nowrap'
      }}
      disabled={isDisabled}
      fullWidth
      error={error}
      helperText={error ? errorText : ''}
      variant="standard"
      InputProps={{
        inputComponent,
        'data-testid': `search-select-${placeholder}`,
        inputProps: {
          className: input,
          inputRef: innerRef,
          children,
          ...innerProps
        }
      }}
      {...selectProps.textFieldProps}
    />
  );
}

Control.propTypes = {
  selectProps: PropTypes.shape({
    classes: WithStylesClassesPropType,
    textFieldProps: PropTypes.shape({
      strikethrough: PropTypes.bool
    }),
    isDisabled: PropTypes.bool,
    error: PropTypes.bool,
    errorText: PropTypes.string,
    placeholder: PropTypes.string
  }),
  innerRef: PropTypes.node,
  children: ReactChildrenPropType,
  innerProps: PropTypes.shape({})
};

Control.defaultProps = {
  selectProps: {},
  children: undefined,
  innerProps: {},
  innerRef: undefined
};

function Option(props) {
  const { innerRef, isFocused, isSelected, innerProps, children, data, isDisabled } = props;
  const { adornment } = data || {};
  const { value = '', color = 'primary' } = adornment || {};

  const item = !adornment ? (
    children
  ) : (
    <span>
      {children}
      <StyledCopyMini color={color} tagName="span">
        {value}
      </StyledCopyMini>
    </span>
  );

  return (
    <MenuItem
      disabled={isDisabled}
      buttonRef={innerRef}
      selected={isFocused}
      component="div"
      style={{
        fontSize: 'inherit',
        fontWeight: isSelected ? 500 : 400
      }}
      {...innerProps}
      title={item}
    >
      {item}
    </MenuItem>
  );
}

Option.propTypes = {
  selectProps: PropTypes.shape({
    error: PropTypes.bool,
    classes: WithStylesClassesPropType,
    isRequired: PropTypes.bool
  }),
  children: ReactChildrenPropType,
  innerProps: PropTypes.shape({}),
  innerRef: PropTypes.node,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
  data: PropTypes.shape({
    adornment: PropTypes.shape({})
  }),
  isDisabled: PropTypes.bool
};

Option.defaultProps = {
  selectProps: {},
  children: undefined,
  innerProps: {},
  innerRef: undefined,
  isFocused: false,
  isSelected: false,
  data: {},
  isDisabled: false
};

function Placeholder(props) {
  const {
    selectProps: {
      isRequired,
      error,
      classes: { placeholder, palceholderError }
    },
    innerProps,
    children
  } = props;

  return (
    <Typography color="textSecondary" className={error ? palceholderError : placeholder} {...innerProps}>
      {children} {isRequired ? '*' : ''}
    </Typography>
  );
}

Placeholder.propTypes = {
  selectProps: PropTypes.shape({
    error: PropTypes.bool,
    classes: WithStylesClassesPropType,
    isRequired: PropTypes.bool
  }),
  children: ReactChildrenPropType,
  innerProps: PropTypes.shape({})
};

Placeholder.defaultProps = {
  selectProps: {},
  children: undefined,
  innerProps: {}
};

function SingleValue(props) {
  const { selectProps, innerProps, children } = props;

  const { isDisabled } = selectProps;
  return (
    <Typography
      {...innerProps}
      style={{ color: isDisabled ? colors.colorGrey03 : colors.colorBlack, fontSize: 'inherit' }}
    >
      {children}
    </Typography>
  );
}

SingleValue.propTypes = {
  selectProps: PropTypes.shape({
    isDisabled: PropTypes.bool
  }),
  children: ReactChildrenPropType,
  innerProps: PropTypes.shape({})
};

SingleValue.defaultProps = {
  selectProps: {},
  children: undefined,
  innerProps: {}
};

function ValueContainer(props) {
  const {
    selectProps: {
      classes: { valueContainer }
    },
    children
  } = props;
  return <div className={valueContainer}>{children}</div>;
}

ValueContainer.propTypes = {
  selectProps: PropTypes.shape({
    classes: PropTypes.string
  }),
  children: ReactChildrenPropType.node
};

ValueContainer.defaultProps = {
  selectProps: {},
  children: undefined
};

function Menu(props) {
  const {
    selectProps: {
      classes: { paper }
    },
    innerProps,
    children
  } = props;

  return (
    <Grow in>
      <Paper elevation={3} className={paper} {...innerProps}>
        {children}
      </Paper>
    </Grow>
  );
}

Menu.propTypes = {
  selectProps: PropTypes.shape({
    classes: PropTypes.string
  }),
  children: ReactChildrenPropType,
  innerProps: PropTypes.shape({})
};

Menu.defaultProps = {
  selectProps: {},
  children: undefined,
  innerProps: {}
};

function DropdownIndicator() {
  return (
    <SvgIcon>
      <path fill="#8D8D8D" d="M7,10L12,15L17,10H7Z" />
    </SvgIcon>
  );
}

const components = {
  Control,
  Menu,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator
};

const customizeMuiGlobalTheme = () => {
  const newSelectedTheme = cloneDeep(muiGlobalTheme);
  newSelectedTheme.palette.text.secondary = colors.colorGrey03;

  newSelectedTheme.components = {
    ...newSelectedTheme.components,
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: colors.colorBlack,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)'
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }
          }
        }
      }
    }
  };

  return createTheme(newSelectedTheme);
};

function IntegrationReactSelect(props) {
  const {
    className,
    classes,
    label,
    options,
    onChange,
    error,
    errorText,
    required,
    value,
    disabled,
    multiple,
    strikethrough,
    styles,
    name,
    setFieldTouched
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [selectionMade, setSelectionMade] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');

  useLayoutEffect(() => {
    // eslint-disable-next-line global-require
    Select = require('react-select').default;
    setIsLoading(false);
  }, []);

  const selectTheme = useRef(customizeMuiGlobalTheme());

  const handleOnChange = (identity) => {
    /**
     * react-select does not emit a JS event, so the identity variable should be handled in our codebase like below.
     * (https://github.com/JedWatson/react-select/issues/1631#issuecomment-555986192)
     */
    const newValue = identity === null ? '' : identity.value || identity.id;
    onChange({ target: { name, value: newValue } });
    setFieldTouched(name, true);

    setSelectionMade(!!identity);
  };

  return (
    <div className={className}>
      {!isLoading && (
        <NoSsr>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={selectTheme.current}>
              <Select
                isDisabled={disabled}
                value={value}
                isRequired={required}
                onInputChange={(input) => setCurrentQuery(input)}
                isClearable
                classes={classes}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  clearIndicator: () => ({ paddingTop: '6px', cursor: 'pointer' }),
                  ...styles
                }}
                options={options}
                textFieldProps={{
                  label: selectionMade || value ? label : ' ',
                  InputLabelProps: {
                    error,
                    shrink: true,
                    style: { fontSize: '1rem' }
                  },
                  strikethrough
                }}
                onKeyDown={(e) => {
                  if (e.keyCode === 32 && currentQuery === '') e.preventDefault();
                }}
                components={components}
                onChange={(identity) => handleOnChange(identity)}
                placeholder={label}
                menuPlacement="auto"
                menuPortalTarget={document.body}
                error={error}
                errorText={errorText}
                isMulti={multiple}
              />
            </ThemeProvider>
          </StyledEngineProvider>
        </NoSsr>
      )}
    </div>
  );
}

IntegrationReactSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  classes: WithStylesClassesPropType.isRequired,
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      adornment: PropTypes.shape({
        value: PropTypes.string,
        color: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'quaternary', 'alert', 'error', 'pending'])
      })
    })
  ).isRequired,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  value: PropTypes.shape({}),
  multiple: PropTypes.bool,
  strikethrough: PropTypes.bool,
  styles: PropTypes.objectOf(PropTypes.string),
  name: PropTypes.string.isRequired,
  setFieldTouched: PropTypes.string
};

IntegrationReactSelect.defaultProps = {
  className: undefined,
  errorText: 'Please make a selection',
  error: false,
  required: false,
  disabled: false,
  value: undefined,
  multiple: false,
  strikethrough: undefined,
  styles: {},
  setFieldTouched: () => {}
};

const composedIntegrationReactSelect = withStyles(baseStyles)(IntegrationReactSelect);

export default composedIntegrationReactSelect;
